import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalCustomerServiceAnalytics from "@faire/web-api--source/routes/brand-portal/analytics/customer-service";
import brandPortalMarketplace from "@faire/web-api--source/routes/brand-portal/marketplace";
import brandPortalShopSettings from "@faire/web-api--source/routes/brand-portal/my-shop/settings";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const ShopLeadTimeIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="Your <BoldFormatting>shop lead time</BoldFormatting> lets retailers know how long it will take for your shop to fulfill a new order. Setting an accurate lead time helps set the right expectations with customers. By meeting your shop lead time, your orders are considered on time. You can track how often orders are late on your <LinkFormatting>Customer service</LinkFormatting> analytics page."
          values={{
            LinkFormatting: CustomerServiceAnalyticsLinkFormatting,
            BoldFormatting,
          }}
          description={{
            text: "Introduction to shop lead time",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you haven’t set your shop lead time, Faire will automatically set one based on how quickly your shop has fulfilled orders over the last 90 days."
          description={{
            text: "Information about automatic lead time setting",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To edit your shop lead time:"
          description={{
            text: "Header for editing shop lead time steps",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to the <LinkFormatting>Marketplace</LinkFormatting> tab."
              values={{
                LinkFormatting: MarketplaceLinkFormatting,
              }}
              description={{
                text: "First step to edit shop lead time",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <LinkFormatting>Shop settings</LinkFormatting>."
              values={{
                LinkFormatting: ShopSettingsLinkFormatting,
              }}
              description={{
                text: "Second step to edit shop lead time",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Scroll to <BoldFormatting>Shop lead time</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to edit shop lead time",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select a lead time from the dropdown menu."
              description={{
                text: "Fourth step to edit shop lead time",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Save</BoldFormatting> any changes."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fifth step to edit shop lead time",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
    </Column>
  );
};

const CustomerServiceAnalyticsLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={brandPortalCustomerServiceAnalytics()}
    target="_blank"
  >
    <Bold>{chunks}</Bold>
  </Button>
);

const ShopSettingsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalShopSettings()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const MarketplaceLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalMarketplace()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
